import { useState, useEffect } from 'react';
import { authAPI } from './api';

// Badge colour cycling for dynamically-defined types
const COLORS = [
  'bg-blue-100 text-blue-700 border-blue-300',
  'bg-cyan-100 text-cyan-700 border-cyan-300',
  'bg-violet-100 text-violet-700 border-violet-300',
  'bg-amber-100 text-amber-700 border-amber-300',
  'bg-emerald-100 text-emerald-700 border-emerald-300',
  'bg-rose-100 text-rose-700 border-rose-300',
  'bg-orange-100 text-orange-700 border-orange-300',
  'bg-teal-100 text-teal-700 border-teal-300',
];

// Slugify a display label so it can be stored/compared as a stable ID.
// "Music Companies" → "music_companies"
export const slugify = (label) =>
  label.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

/**
 * Returns the company's custom contact types as an array of
 *   { id, label, color }
 * where id is the slugified label (for filter params) or the raw stored value.
 *
 * Falls back to an empty array so callers always get a defined list.
 * The "All" entry is NOT included — callers add it themselves if needed.
 */
export function useContactTypes() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authAPI.getMe()
      .then(res => {
        const raw = res.data?.company?.contact_types || [];
        const mapped = raw.map((label, i) => ({
          id: slugify(label),
          label,
          color: COLORS[i % COLORS.length],
        }));
        setTypes(mapped);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Helper: find colour by stored contact_type value (handles both slugs and plain labels)
  const colorFor = (value) => {
    if (!value) return 'bg-gray-100 text-gray-600 border-gray-300';
    const match = types.find(t => t.id === value || t.label === value || slugify(t.label) === slugify(value));
    return match?.color || 'bg-gray-100 text-gray-600 border-gray-300';
  };

  const labelFor = (value) => {
    if (!value) return '';
    const match = types.find(t => t.id === value || t.label === value || slugify(t.label) === slugify(value));
    return match?.label || value;
  };

  return { types, loading, colorFor, labelFor };
}
